<template>
  <!-- The Workspace369 app on an iPad and an iPhone, the same workspace on both (Workspace369
       origin/main, Oct 22 2026). The iPad runs the Invoices screen (InvoicesAppWindow, the app's
       wide layout). The iPhone runs the app's phone layout (under 850pt, responsive_builder.dart):
       - Home: the default dashboard (HomeSwitcher → ProductivityDashboardScreen, v1).
       - Client Preview (client_preview_screen.dart _buildNarrowView): customAppBar (custom_appbar.dart,
         app_back_button.dart's CupertinoIcons.back), the name row, the Quick overview card
         (_buildMobileQuickOverviewCard, no portal button: it shows only when the portal can open; _buildOverviewSummaryGrid: four tiles, compact money from
         NumberFormat.compact, quiet zeros at 40%) and the Client card's field rows
         (client_info_section_widget.dart, app_field_row.dart at touch density: 36pt rows, 120pt label
         column). Client Health is left out: it only shows to the workspace owner.
       - Paid: the client pays invoice 1034; the iPad's row turns Paid and the phone's Quick overview
         drops to "0 · $0" while the page scrolls to its Overview › Invoices row (status "Paid", green tile).
       Labels are app_en.arb strings; the Northstar Studio workspace, clients and amounts are sample data.
       The server renders `paid`. Device frames are drawn: their rounded corners and the camera
       island are the hardware, not site UI. Radius exception: the phone's floating tab bar and its
       selected tab are drawn fully round (31/27pt) as the app's own tab bar; count badges are 5pt chips. -->
  <div class="mbw" :class="`mbw--${state}`" aria-hidden="true">
    <svg class="mbw-sprite" width="0" height="0">
      <symbol v-for="(d, name) in icons" :id="`mbw-${name}`" :key="name" viewBox="0 0 24 24"><path :d="d" /></symbol>
    </svg>

    <!-- iPad, landscape -->
    <div class="mbw-ipad">
      <div class="mbw-ipad-screen">
        <div class="mbw-status"><b>9:41</b><span>Thu Oct 22</span><i class="mbw-grow" /><span class="mbw-batt"><i /></span></div>
        <div class="mbw-ipad-app"><MotionInvoicesAppWindow :state="ipadState" /></div>
      </div>
    </div>

    <!-- iPhone -->
    <div class="mbw-phone">
      <div class="mbw-phone-screen">
        <div class="mwp">
          <div class="mwp-status"><b>9:41</b><i class="mwp-island" /><span class="mbw-batt"><i /></span></div>

          <!-- Home -->
          <section class="mwp-view mwp-home" :class="{ on: state === 'home' }">
            <header class="mph-bar">
              <svg class="ic"><use href="#mbw-view_sidebar" /></svg>
              <i class="mbw-grow" />
              <span class="mph-search"><svg class="ic"><use href="#mbw-search" /></svg></span>
              <span class="mph-bell"><svg class="ic"><use href="#mbw-notifications" /></svg><b>3</b></span>
            </header>
            <div class="mph-body">
              <div class="mph-greet">
                <div class="mph-hello">
                  <b>Good morning</b>
                  <span class="mph-score-row"><span class="mph-score"><i /><span>92</span></span><span class="mph-coach">Excellent! Keep up the great work!</span></span>
                </div>
                <span class="mph-btn"><svg class="ic"><use href="#mbw-add" /></svg></span>
                <span class="mph-btn"><svg class="ic"><use href="#mbw-tune" /></svg></span>
              </div>
              <div class="mph-period">
                <span class="mph-seg"><span>7D</span><span>30D</span><span class="on">MTD</span><span>YTD</span></span>
                <span class="mph-custom"><svg class="ic"><use href="#mbw-calendar_today" /></svg><svg class="ic"><use href="#mbw-expand_more" /></svg></span>
              </div>
              <div class="mph-two">
                <div class="mph-card"><small>Tasks due today</small><b>3</b></div>
                <div class="mph-card"><small>Events today</small><b>2</b></div>
              </div>
              <div class="mph-card mph-card--wide"><small>Upcoming reminders</small><b>4</b></div>
              <div class="mph-grid">
                <div v-for="b in boxes" :key="b.t" class="mph-box" :style="{ '--accent': b.c }">
                  <span class="mph-box-top"><small>{{ b.t }}</small><svg class="ic"><use href="#mbw-arrow_outward" /></svg></span>
                  <b>{{ b.v }}</b><em>{{ b.u }}</em>
                  <span class="mph-spark"><i v-for="(h, k) in b.s" :key="k" :style="{ height: `${h}%` }" /></span>
                  <span v-if="b.d" class="mph-trend"><svg class="ic"><use :href="`#mbw-${b.down ? 'arrow_downward' : 'arrow_upward'}`" /></svg>{{ b.d }}<span>vs last month</span></span>
                </div>
              </div>
            </div>
            <!-- iOS 26 draws the tab bar natively (Liquid Glass, liquid_bottom_navigation_bar.dart); its
                 capsule shape is the system's, not site UI. Icons are the app's Material fallbacks. -->
            <nav class="mph-nav">
              <span class="mph-tabs">
                <span v-for="t in navTabs" :key="t.k" :class="{ on: t.k === 'Home' }"><svg class="ic"><use :href="`#mbw-${t.i}`" /></svg>{{ t.k }}</span>
              </span>
              <span class="mph-more"><svg class="ic"><use href="#mbw-add" /></svg></span>
            </nav>
          </section>

          <!-- Client Preview -->
          <section class="mwp-view mwp-client" :class="{ on: state !== 'home' }">
            <header class="mpc-bar">
              <span class="mpc-back"><svg class="ic"><use href="#mbw-back_chevron" /></svg></span>
              <b>Client Preview</b>
              <span class="mpc-acts">
                <span class="mpc-icbtn"><svg class="ic"><use href="#mbw-edit" /></svg></span>
                <span class="mpc-icbtn"><svg class="ic"><use href="#mbw-more_vert" /></svg></span>
              </span>
            </header>
            <div class="mpc-scroll">
              <div class="mpc-body">
                <div class="mpc-name"><b>Ava Marlowe</b><span class="mpc-avatar"><svg class="ic"><use href="#mbw-person" /></svg></span></div>

                <div class="mpc-card">
                  <div class="mpc-card-head"><b>Quick overview</b><span>Tap to view</span></div>
                  <div class="mpc-grid">
                    <div v-for="t in tiles" :key="t.l" class="mpc-tile">
                      <svg class="ic"><use :href="`#mbw-${t.i}`" /></svg>
                      <span><b :class="{ quiet: t.quiet }">{{ t.v }}</b><small>{{ t.l }}</small></span>
                    </div>
                  </div>
                </div>

                <div class="mpc-card">
                  <div class="mpc-card-head mpc-card-head--lg"><b>Client</b><svg class="ic"><use href="#mbw-more_vert" /></svg></div>
                  <div v-for="f in fields" :key="f.l" class="mpc-field">
                    <span class="mpc-label"><svg class="ic"><use :href="`#mbw-${f.i}`" /></svg><span>{{ f.l }}</span></span>
                    <span class="mpc-value">{{ f.v }}</span>
                    <span v-if="f.copy" class="mpc-copy"><svg class="ic"><use href="#mbw-content_copy" /></svg></span>
                  </div>
                </div>

                <div class="mpc-card mpc-overview">
                  <div class="mpc-card-head mpc-card-head--lg"><b>Overview</b><span>Updated Oct 22</span></div>
                  <div class="mpc-tabs"><span class="on">Overview</span><span>Billing</span></div>
                  <div class="mpc-sec">
                    <div class="mpc-sec-head"><span class="mpc-sec-ic"><svg class="ic"><use href="#mbw-receipt_long" /></svg></span><b>Invoices</b><em>1</em></div>
                    <div class="mpc-row">
                      <span class="mpc-row-ic"><svg class="ic"><use href="#mbw-receipt_long" /></svg></span>
                      <span class="mpc-row-t"><b>Invoice 1034 • Brand identity deposit (40%)</b><small>{{ invoice.status }} • Oct 2</small></span>
                      <span class="mpc-row-amt">$4.8K</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { appIcons } from "~/data/app-icons.js";
import { mobileIcons } from "~/data/app-icons-mobile.js";

const props = defineProps({
  // home | client | paid
  state: { type: String, default: "paid" },
});

const shared = ["add", "auto_awesome", "calendar_today", "dashboard", "description", "expand_more", "more_vert", "notifications", "person", "receipt_long", "search", "view_sidebar", "workspaces"];

// Home (productivity_dashboard_screen.dart): the four boxes with their accents, units and MTD trend.
const boxes = [
  { t: "Revenue", v: "$18.4K", u: "paid", c: "#10b981", s: [30, 42, 38, 55, 48, 62, 100], d: "18%" },
  { t: "Clients", v: "52", u: "total", c: "#171717", s: [70, 72, 74, 78, 80, 84, 100], d: "8%" },
  { t: "Expenses", v: "$2,140", u: "this month", c: "#ff9800", s: [60, 40, 52, 46, 70, 38, 100], d: "4%", down: true },
  { t: "Invoices", v: "14", u: "paid", c: "#673ab7", s: [20, 35, 30, 45, 50, 60, 100], d: "12%" },
];
// bottom_nav_config_model.dart default tabs.
const navTabs = [
  { k: "Home", i: "dashboard" },
  { k: "Projects", i: "workspaces" },
  { k: "AI AutoPilot", i: "auto_awesome" },
  { k: "Invoices", i: "receipt_long" },
  { k: "Calendar", i: "calendar_today" },
];
const icons = { ...Object.fromEntries(shared.map((k) => [k, appIcons[k]])), ...mobileIcons };

// The iPad's Invoices screen: invoice 1034 is open until the client pays it.
const ipadState = computed(() => (props.state === "paid" ? "paid" : "open"));

const paid = computed(() => props.state === "paid");
// _buildOverviewSummaryGrid: open count · compact outstanding, proposals, next visit, active projects.
const tiles = computed(() => [
  { i: "receipt_long", l: "Open invoices", v: paid.value ? "0 · $0" : "1 · $4.8K", quiet: paid.value },
  { i: "description", l: "Proposals", v: "1" },
  { i: "calendar_today", l: "Next visit", v: "Oct 29" },
  { i: "folder", l: "Active Projects", v: "1" },
]);
const fields = [
  { i: "apartment", l: "Company Name", v: "Marlowe Coffee Co.", copy: true },
  { i: "mail_outline", l: "Email", v: "ava@marlowecoffee.example", copy: true },
  { i: "phone", l: "Phone Number", v: "+15125550148", copy: true },
  { i: "lead_route", l: "Lead Source", v: "Referral" },
];
// _buildInvoiceOverviewRow: "{status} • {date}", the status tints the icon tile (orange open, green paid).
const invoice = computed(() => (paid.value ? { status: "Paid" } : { status: "Open" }));
</script>

<style>
/* Tokens: shadcn Neutral (the app's default scheme, FlexScheme index 58) with the client page's
   section, inset and border tones (client_preview_screen.dart _clientPreview* helpers). */
.mbw {
  --ink: #0a0a0a; --ink2: #5f5f5f; --muted: #737373; --page: #fafafa; --section: #fff; --inset: #fff;
  --line: rgb(212 212 212 / 0.55); --bar: #ececec; --btn: #fff; --secondary: #f5f5f5;
  --bezel: #0d0e10; --rim: #3a3d42;
  position: relative;
  container: mbw / inline-size;
  aspect-ratio: 1248 / 788;
  color: var(--ink);
  font-family: "Space Grotesk", ui-sans-serif, system-ui, sans-serif;
  font-variant-numeric: tabular-nums;
  line-height: 1.3;
  text-align: left;
}
.dark .mbw {
  --ink: #fafafa; --ink2: #b5b5b5; --muted: #a3a3a3; --page: #1c1c1c; --section: #232323; --inset: rgb(46 46 46 / 0.7);
  --line: rgb(82 82 82 / 0.42); --bar: #2e2e2e; --btn: rgb(46 46 46 / 0.86); --secondary: #262626;
  --bezel: #050506; --rim: #6b7078;
}
.mbw-sprite { position: absolute; width: 0; height: 0; overflow: hidden; }
.mbw .ic { display: inline-block; flex: none; width: 18px; height: 18px; fill: currentColor; vertical-align: middle; }
.mbw-grow { flex: 1; }

/* ---------- iPad (11-inch, landscape: 1194 × 834 pt) ---------- */
.mbw-ipad {
  position: absolute; top: 0; left: 0; width: 76%;
  aspect-ratio: 1242 / 882;
  border-radius: 3.4% / 4.8%;
  background: var(--bezel);
  padding: 2%;
  box-shadow: inset 0 0 0 1.5px var(--rim), 0 40px 80px -40px rgb(15 23 42 / 0.45), 0 16px 32px -20px rgb(15 23 42 / 0.2);
}
.mbw-ipad-screen { display: flex; flex-direction: column; height: 100%; overflow: hidden; border-radius: 1.6% / 2.3%; background: var(--page); }
.mbw-status { display: flex; flex: none; align-items: center; gap: 8px; height: 22px; padding: 0 18px; color: var(--ink); font-size: 12px; }
.mbw-status span:not(.mbw-batt) { color: var(--ink2); }
.mbw-batt { position: relative; display: inline-block; width: 22px; height: 11px; border: 1px solid currentColor; border-radius: 3px; opacity: 0.8; }
.mbw-batt i { position: absolute; inset: 1.5px 5px 1.5px 1.5px; border-radius: 1px; background: currentColor; }
.mbw-ipad-app { flex: 1; min-height: 0; }
/* The Invoices window fills the screen: no window chrome of its own inside the device. */
.mbw-ipad-app .iaw { height: 100%; min-height: 0; }
.mbw-ipad-app .iaw-desk { height: 100%; border: 0; border-radius: 0; box-shadow: none; -webkit-mask-image: none; mask-image: none; }
.dark .mbw-ipad-app .iaw-desk { box-shadow: none; }

/* ---------- iPhone (6.3-inch: 402 × 874 pt) ---------- */
.mbw-phone {
  position: absolute; top: 6.5%; right: 0; width: 27%;
  aspect-ratio: 432 / 904;
  border-radius: 15.5% / 7.4%;
  background: var(--bezel);
  padding: 3.4%;
  box-shadow: inset 0 0 0 1.5px var(--rim), 0 40px 70px -30px rgb(15 23 42 / 0.55), 0 14px 28px -16px rgb(15 23 42 / 0.3);
}
.dark .mbw-phone, .dark .mbw-ipad { box-shadow: inset 0 0 0 1.5px var(--rim), 0 0 0 1px rgb(148 163 184 / 0.22), 0 40px 80px -30px rgb(0 0 0 / 0.8); }
.mbw-phone-screen { height: 100%; overflow: hidden; border-radius: 12.5% / 5.8%; background: var(--page); container-type: inline-size; }

/* One app point = 1/402 of the screen; text at the app's default 125%. */
.mwp { --u: calc(100cqi / 402); --t: calc(var(--u) * 1.25); position: relative; height: 100%; font-size: calc(var(--t) * 12); }
.mwp .ic { width: calc(var(--u) * 22); height: calc(var(--u) * 22); }
.mwp-status { position: absolute; z-index: 4; top: 0; right: 0; left: 0; display: flex; align-items: center; justify-content: space-between; height: calc(var(--u) * 54); padding: 0 calc(var(--u) * 34) 0 calc(var(--u) * 46); font-size: calc(var(--u) * 16); }
.mwp-status .mbw-batt { width: calc(var(--u) * 26); height: calc(var(--u) * 12); border-radius: calc(var(--u) * 4); }
.mwp-island { position: absolute; top: calc(var(--u) * 11); left: 50%; width: calc(var(--u) * 124); height: calc(var(--u) * 36); border-radius: calc(var(--u) * 18); background: #000; translate: -50% 0; }
.mwp-view { position: absolute; inset: 0; opacity: 0; visibility: hidden; transition: opacity 0.45s ease, visibility 0s linear 0.45s; }
.mwp-view.on { opacity: 1; visibility: visible; transition: opacity 0.45s ease; }

/* Home (productivity_dashboard_screen.dart, phone): white page, flat 12pt panels with a 10% hairline
   (dashboard_surface.dart), Inconsolata 800 numbers (dashboard_metric_card.dart). */
.mwp-home { --hpage: #fff; --hline: rgb(10 10 10 / 0.1); background: var(--hpage); }
.dark .mwp-home { --hpage: #161616; --hline: rgb(250 250 250 / 0.09); }
.mph-bar { display: flex; align-items: center; gap: calc(var(--u) * 8); height: calc(var(--u) * 56); margin-top: calc(var(--u) * 54); background: rgb(10 10 10 / 0.025); padding: 0 calc(var(--u) * 16) 0 calc(var(--u) * 14); }
.dark .mph-bar { background: rgb(250 250 250 / 0.02); }
.mph-search, .mph-bell { position: relative; display: grid; place-items: center; width: calc(var(--u) * 36); height: calc(var(--u) * 35); border-radius: calc(var(--u) * 8); background: var(--section); }
.mph-bell { border-radius: calc(var(--u) * 12); }
.mph-bar .ic { width: calc(var(--u) * 20); height: calc(var(--u) * 20); }
.mph-bell b { position: absolute; top: calc(var(--u) * -6); right: calc(var(--u) * -6); display: grid; place-items: center; min-width: calc(var(--u) * 16); height: calc(var(--u) * 16); border-radius: calc(var(--u) * 5); background: #ef4444; color: #fff; font-size: calc(var(--u) * 9); }
.mph-body { display: grid; gap: calc(var(--u) * 8); padding: calc(var(--u) * 16) calc(var(--u) * 8) 0; }
.mph-greet { display: flex; align-items: flex-start; gap: calc(var(--u) * 8); padding: 0 calc(var(--u) * 4); }
.mph-hello { display: grid; flex: 1; gap: calc(var(--u) * 4); min-width: 0; }
.mph-hello > b { font-size: calc(var(--t) * 20); line-height: 1.15; white-space: nowrap; }
.mph-score-row { display: flex; align-items: center; gap: calc(var(--u) * 8); }
/* The score gauge: 80+ draws in the scheme's secondary (#f5f5f5 light, #262626 dark), as the app does. */
.mph-score { position: relative; display: grid; flex: none; place-items: center; width: calc(var(--u) * 26); height: calc(var(--u) * 26); border-radius: 50%; background: conic-gradient(var(--secondary) 0 92%, color-mix(in srgb, var(--secondary) 18%, transparent) 0); }
.mph-score::after { content: ""; position: absolute; inset: calc(var(--u) * 2.5); border-radius: 50%; background: var(--hpage); }
.mph-score span { position: relative; z-index: 1; font-size: calc(var(--u) * 10); font-weight: 700; }
.mph-coach { overflow: hidden; color: rgb(10 10 10 / 0.62); font-size: calc(var(--t) * 12); line-height: 1.25; }
.dark .mph-coach { color: rgb(250 250 250 / 0.62); }
.mph-btn { display: grid; flex: none; place-items: center; width: calc(var(--u) * 44); height: calc(var(--u) * 44); border: 1px solid var(--hline); border-radius: calc(var(--u) * 12); background: var(--hpage); }
.mph-btn .ic { width: calc(var(--u) * 20); height: calc(var(--u) * 20); }
.mph-period { display: flex; align-items: center; gap: calc(var(--u) * 8); margin-top: calc(var(--u) * 2); }
.mph-seg { display: grid; flex: 1; grid-template-columns: repeat(4, 1fr); height: calc(var(--u) * 30); border-radius: calc(var(--u) * 10); background: rgb(10 10 10 / 0.06); padding: calc(var(--u) * 3); }
.dark .mph-seg { background: rgb(250 250 250 / 0.06); }
.mph-seg span { display: grid; place-items: center; border-radius: calc(var(--u) * 8); color: rgb(10 10 10 / 0.6); font-size: calc(var(--t) * 12); font-weight: 500; }
.dark .mph-seg span { color: rgb(250 250 250 / 0.6); }
.mph-seg .on { border: 1px solid rgb(10 10 10 / 0.08); background: #fff; box-shadow: 0 1px 4px rgb(0 0 0 / 0.1); color: var(--ink); font-weight: 600; }
.dark .mph-seg .on { border-color: rgb(250 250 250 / 0.08); background: #333; color: var(--ink); }
.mph-custom { display: inline-flex; align-items: center; gap: calc(var(--u) * 2); height: calc(var(--u) * 30); border: 1px solid rgb(115 115 115 / 0.35); border-radius: calc(var(--u) * 10); padding: 0 calc(var(--u) * 9); }
.mph-custom .ic { width: calc(var(--u) * 14); height: calc(var(--u) * 14); }
.mph-custom .ic + .ic { width: calc(var(--u) * 15); height: calc(var(--u) * 15); }
.mph-two { display: grid; grid-template-columns: 1fr 1fr; gap: calc(var(--u) * 8); }
.mph-card { display: grid; gap: calc(var(--u) * 6); border: 1px solid var(--hline); border-radius: calc(var(--u) * 12); background: var(--hpage); padding: calc(var(--u) * 10); }
.mph-card small, .mph-box small { color: rgb(115 115 115 / 0.85); font-size: calc(var(--u) * 10); font-weight: 500; letter-spacing: 0.1em; text-transform: uppercase; }
.dark .mph-card small, .dark .mph-box small { color: rgb(163 163 163 / 0.85); }
.mph-card b, .mph-box b { font-family: Inconsolata, ui-monospace, "SF Mono", Menlo, monospace; font-weight: 800; letter-spacing: -0.04em; line-height: 1; }
.mph-card b { font-size: calc(var(--u) * 28); }
.mph-card--wide { gap: calc(var(--u) * 16); padding: calc(var(--u) * 20) calc(var(--u) * 24); }
.mph-card--wide small { font-size: calc(var(--u) * 13); letter-spacing: 0.15em; }
.mph-card--wide b { font-size: calc(var(--u) * 48); }
.mph-grid { display: grid; grid-template-columns: 1fr 1fr; gap: calc(var(--u) * 8); margin-top: calc(var(--u) * 2); }
.mph-box { display: flex; flex-direction: column; align-items: center; aspect-ratio: 1; border: 1px solid var(--hline); border-radius: calc(var(--u) * 12); background: var(--hpage); padding: calc(var(--u) * 10); }
.mph-box-top { display: flex; align-self: stretch; align-items: center; justify-content: space-between; }
.mph-box small { font-size: calc(var(--u) * 9); }
.mph-box-top .ic { width: calc(var(--u) * 14); height: calc(var(--u) * 14); opacity: 0.38; }
.mph-box b { margin-top: calc(var(--u) * 14); font-size: calc(var(--u) * 26); }
.mph-box em { margin-top: calc(var(--u) * 3); color: rgb(115 115 115 / 0.6); font-size: calc(var(--u) * 9); font-style: normal; }
.mph-spark { display: flex; align-items: flex-end; gap: calc(var(--u) * 6); height: calc(var(--u) * 20); margin-top: auto; }
.mph-spark i { width: calc(var(--u) * 10); border-radius: calc(var(--u) * 3); background: rgb(115 115 115 / 0.25); }
.mph-spark i:last-child { background: var(--accent); }
.dark .mph-box[style*="#171717"] .mph-spark i:last-child { background: #fafafa; }
.mph-trend { display: flex; align-items: center; gap: calc(var(--u) * 2); margin-top: calc(var(--u) * 6); color: #10b981; font-size: calc(var(--u) * 10); font-weight: 700; }
.mph-trend .ic { width: calc(var(--u) * 11); height: calc(var(--u) * 11); }
.mph-trend span { margin-left: calc(var(--u) * 3); color: var(--muted); font-size: calc(var(--u) * 9); font-weight: 400; }
.mph-nav { position: absolute; z-index: 3; right: calc(var(--u) * 14); bottom: calc(var(--u) * 22); left: calc(var(--u) * 14); display: flex; align-items: center; gap: calc(var(--u) * 10); }
.mph-tabs { display: flex; flex: 1; height: calc(var(--u) * 62); border: 1px solid rgb(255 255 255 / 0.6); border-radius: calc(var(--u) * 31); background: rgb(250 250 250 / 0.82); padding: calc(var(--u) * 4); box-shadow: 0 8px 24px rgb(0 0 0 / 0.12); -webkit-backdrop-filter: blur(12px); backdrop-filter: blur(12px); }
.dark .mph-tabs, .dark .mph-more { border-color: rgb(255 255 255 / 0.1); background: rgb(40 40 40 / 0.82); }
.mph-tabs > span { display: grid; flex: 1; place-content: center; justify-items: center; gap: calc(var(--u) * 2); border-radius: calc(var(--u) * 27); color: rgb(10 10 10 / 0.6); font-size: calc(var(--u) * 10); white-space: nowrap; }
.dark .mph-tabs > span { color: rgb(250 250 250 / 0.6); }
.mph-tabs .on { background: rgb(10 10 10 / 0.07); color: var(--ink); font-weight: 600; }
.dark .mph-tabs .on { background: rgb(250 250 250 / 0.1); }
.mph-tabs .ic { width: calc(var(--u) * 22); height: calc(var(--u) * 22); }
.mph-more { display: grid; flex: none; place-items: center; width: calc(var(--u) * 54); height: calc(var(--u) * 54); border: 1px solid rgb(255 255 255 / 0.6); border-radius: 50%; background: rgb(250 250 250 / 0.82); color: rgb(10 10 10 / 0.7); box-shadow: 0 8px 24px rgb(0 0 0 / 0.12); }
.dark .mph-more { color: rgb(250 250 250 / 0.7); }

/* Client Preview */
.mpc-bar { position: absolute; z-index: 3; top: 0; right: 0; left: 0; display: flex; align-items: center; gap: calc(var(--u) * 8); height: calc(var(--u) * 110); border-radius: 0 0 calc(var(--u) * 10) calc(var(--u) * 10); background: var(--bar); padding: calc(var(--u) * 54) calc(var(--u) * 12) 0 calc(var(--u) * 10); }
.mpc-bar > b { position: absolute; left: 50%; bottom: calc(var(--u) * 18); font-size: calc(var(--t) * 16); translate: -50% 0; white-space: nowrap; }
.mpc-back { display: grid; place-items: center; width: calc(var(--u) * 36); height: calc(var(--u) * 36); }
.mpc-acts { display: flex; gap: calc(var(--u) * 8); margin-left: auto; }
.mpc-icbtn { display: grid; place-items: center; width: calc(var(--u) * 38); height: calc(var(--u) * 38); border: 1px solid var(--line); border-radius: calc(var(--u) * 10); background: var(--btn); box-shadow: 0 3px 10px rgb(0 0 0 / 0.06); }
.mpc-icbtn .ic { width: calc(var(--u) * 21); height: calc(var(--u) * 21); }
.mpc-scroll { position: absolute; inset: calc(var(--u) * 110) 0 0; overflow: hidden; }
/* Paid: the page scrolls a little, as a finger would, keeping the name and bringing the paid row clear of the edge. A transform, so nothing resizes. */
.mpc-body { display: grid; grid-template-columns: minmax(0, 1fr); gap: calc(var(--u) * 20); padding: calc(var(--u) * 24) calc(var(--u) * 20) calc(var(--u) * 20); transition: transform 1s cubic-bezier(0.65, 0, 0.35, 1) 0.5s; }
.mbw--paid .mpc-body { transform: translateY(calc(var(--u) * -26)); }
.mpc-name { display: flex; align-items: center; gap: calc(var(--u) * 10); padding-left: calc(var(--u) * 10); }
.mpc-name b { flex: 1; font-size: calc(var(--t) * 18); }
.mpc-avatar { display: grid; place-items: center; margin: calc(var(--u) * 5); border-radius: calc(var(--u) * 10); background: var(--secondary); padding: calc(var(--u) * 10); }
.mpc-avatar .ic { width: calc(var(--u) * 24); height: calc(var(--u) * 24); }
.mpc-card { min-width: 0; border: 1px solid var(--line); border-radius: calc(var(--u) * 10); background: var(--section); padding: calc(var(--u) * 14); box-shadow: 0 3px 12px rgb(0 0 0 / 0.08); }
.dark .mpc-card { box-shadow: none; }
.mpc-card-head { display: flex; align-items: center; gap: calc(var(--u) * 8); }
.mpc-card-head b { flex: 1; font-size: calc(var(--t) * 14); }
.mpc-card-head span { color: var(--muted); }
.mpc-card-head--lg b { font-size: calc(var(--t) * 16); }
.mpc-card-head .ic { color: var(--ink2); }
.mpc-grid { display: grid; grid-template-columns: 1fr 1fr; gap: calc(var(--u) * 10); margin-top: calc(var(--u) * 12); }
.mpc-tile { display: flex; align-items: center; gap: calc(var(--u) * 8); min-width: 0; border: 1px solid var(--line); border-radius: calc(var(--u) * 8); background: var(--inset); padding: calc(var(--u) * 10); }
.mpc-tile .ic { width: calc(var(--u) * 18); height: calc(var(--u) * 18); color: var(--muted); }
.mpc-tile > span { display: grid; gap: calc(var(--u) * 2); min-width: 0; }
.mpc-tile b { font-size: calc(var(--t) * 14); white-space: nowrap; transition: color 0.4s ease; }
.mpc-tile b.quiet { color: rgb(10 10 10 / 0.4); }
.dark .mpc-tile b.quiet { color: rgb(250 250 250 / 0.4); }
.mpc-tile small { overflow: hidden; color: var(--muted); font-size: calc(var(--t) * 12); text-overflow: ellipsis; white-space: nowrap; }
.mpc-field { display: flex; align-items: center; gap: calc(var(--u) * 8); height: calc(var(--u) * 36); margin-top: calc(var(--u) * 2); padding: 0 calc(var(--u) * 8); }
.mpc-card-head + .mpc-field { margin-top: calc(var(--u) * 10); }
.mpc-label { display: flex; flex: none; align-items: center; gap: calc(var(--u) * 6); width: calc(var(--u) * 120); overflow: hidden; color: rgb(10 10 10 / 0.6); font-size: calc(var(--t) * 13); white-space: nowrap; }
.dark .mpc-label { color: rgb(250 250 250 / 0.6); }
.mpc-label > span { overflow: hidden; text-overflow: ellipsis; }
.mpc-label .ic { width: calc(var(--u) * 16); height: calc(var(--u) * 16); opacity: 0.67; }
.mpc-value { flex: 1; min-width: 0; overflow: hidden; font-size: calc(var(--t) * 13); text-overflow: ellipsis; white-space: nowrap; }
.mpc-copy { display: grid; place-items: center; width: calc(var(--u) * 32); height: calc(var(--u) * 32); color: var(--muted); }
.mpc-copy .ic { width: calc(var(--u) * 16); height: calc(var(--u) * 16); }
.mpc-overview { padding: calc(var(--u) * 16) 0 calc(var(--u) * 4); }
.mpc-overview .mpc-card-head { padding: 0 calc(var(--u) * 16); }
.mpc-tabs { display: flex; gap: calc(var(--u) * 20); margin-top: calc(var(--u) * 12); border-bottom: 1px solid var(--line); padding: 0 calc(var(--u) * 16); color: var(--muted); font-size: calc(var(--t) * 13); }
.mpc-tabs span { padding-bottom: calc(var(--u) * 8); }
.mpc-tabs .on { box-shadow: inset 0 -2px 0 var(--ink); color: var(--ink); font-weight: 600; }
.mpc-sec { padding: calc(var(--u) * 14) calc(var(--u) * 16); }
.mpc-sec-head { display: flex; align-items: center; gap: calc(var(--u) * 10); margin-bottom: calc(var(--u) * 10); }
.mpc-sec-ic { display: grid; place-items: center; width: calc(var(--u) * 28); height: calc(var(--u) * 28); border-radius: calc(var(--u) * 8); background: rgb(232 146 60 / 0.12); color: #e8923c; }
.mpc-sec-ic .ic { width: calc(var(--u) * 15); height: calc(var(--u) * 15); }
.mpc-sec-head b { font-size: calc(var(--t) * 14); }
/* The count badge: the app draws it fully rounded (radius 99, client_preview_screen.dart _buildStackedSectionHeader). */
/* The count badge is the app's own circular(99) (client_preview_screen.dart). */
.mpc-sec-head em { border-radius: calc(var(--u) * 5); background: rgb(115 115 115 / 0.1); padding: calc(var(--u) * 2) calc(var(--u) * 7); color: var(--muted); font-size: calc(var(--t) * 12); font-style: normal; font-weight: 700; }
.mpc-row { --tone: #ff9800; display: flex; align-items: center; gap: calc(var(--u) * 12); border: 1px solid var(--line); border-radius: calc(var(--u) * 8); background: var(--inset); padding: calc(var(--u) * 10) calc(var(--u) * 12); }
.mbw--paid .mpc-row { --tone: #4caf50; animation: mbw-paid 1.4s ease-out 1.4s backwards; }
/* The moment it is paid, the row on the phone lights up as the iPad's row does (InvoicesAppWindow's wash). */
@keyframes mbw-paid { from { background: color-mix(in srgb, #4caf50 14%, var(--inset)); } to { background: var(--inset); } }
.mpc-row-ic { display: grid; flex: none; place-items: center; width: calc(var(--u) * 34); height: calc(var(--u) * 34); border-radius: calc(var(--u) * 8); background: color-mix(in srgb, var(--tone) 12%, transparent); color: var(--tone); transition: color 0.4s ease, background 0.4s ease; }
.mpc-row-ic .ic { width: calc(var(--u) * 18); height: calc(var(--u) * 18); }
.mpc-row-t { display: grid; flex: 1; gap: calc(var(--u) * 3); min-width: 0; }
.mpc-row-t b { overflow: hidden; font-size: calc(var(--t) * 14); text-overflow: ellipsis; white-space: nowrap; }
.mpc-row-t small { color: var(--muted); font-size: calc(var(--t) * 12); }
.mpc-row-amt { color: var(--muted); font-size: calc(var(--t) * 12); font-weight: 700; }

/* ---------- sizes ---------- */
/* The hero's size reservation: 600 tall on desktop, 540 up to 1128px, 390/700 on a phone. Where the
   devices' own ratio is shorter than that, they sit centred in it (the extra height split above and below). */
/* A definite width, so the min-height is never carried across the aspect ratio into the width. */
.mbw { width: 100%; min-width: 0; min-height: 600px; }
.mbw-ipad, .mbw-phone { translate: 0 max(0px, (600px - 63.14cqi) / 2); }
@media (max-width: 1128px) {
  .mbw { min-height: 540px; }
  .mbw-ipad, .mbw-phone { translate: 0 max(0px, (540px - 63.14cqi) / 2); }
}
/* Laptop widths: the iPad shows the app at its own scale (the collapsed sidebar under 1080pt). */
/* Tablets: the iPad's app is drawn at 1100pt and scaled to fit, so it keeps the wide layout. */
@container mbw (max-width: 1000px) {
  .mbw-ipad-app { zoom: 0.62; }
  .mbw-status { zoom: 0.8; }
}
/* Phones: the iPhone alone, large. */
@media (max-width: 648px) {
  .mbw { max-width: 390px; min-height: 0; margin-inline: auto; aspect-ratio: 390 / 700; }
  .mbw-ipad { display: none; }
  .mbw-phone { top: 0; right: auto; left: 50%; width: auto; height: 100%; translate: -50% 0; }
}
@media (prefers-reduced-motion: reduce) { .mbw *, .mbw *::before { animation: none !important; transition: none !important; } }
</style>
